import { useCallback, useEffect, useState, useRef } from 'react'
import {
  ReactFlow,
  Background,
  MiniMap,
  Panel,
  BackgroundVariant,
  SelectionMode,
  Edge,
  NodeChange,
  Connection,
} from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import { Box, Alert, Snackbar, CircularProgress, useMediaQuery, useTheme, Fab, Tooltip, Chip, Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, List, ListItem, ListItemIcon, ListItemText, Divider } from '@mui/material'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ErrorIcon from '@mui/icons-material/Error'
import WarningIcon from '@mui/icons-material/Warning'
import BuildIcon from '@mui/icons-material/Build'
import DeleteIcon from '@mui/icons-material/Delete'
import RefreshIcon from '@mui/icons-material/Refresh'
import ClearIcon from '@mui/icons-material/Clear'
import EditIcon from '@mui/icons-material/Edit'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useWorkflowStore, WorkflowNode } from '@store/workflowStore'
import { workflowDesignerApi } from '@api/endpoints/workflowDesigner'
import { nodeTypes } from './nodes'
import { WorkflowToolbar } from './WorkflowToolbar'
import { NodePropertiesPanel } from './NodePropertiesPanel'
import { RoutePropertiesPanel } from './RoutePropertiesPanel'
import { ValidationPanel, CompileStatistics } from './ValidationPanel'
import { mapServerValidationToClient } from '@features/adventures/utils/validation/serverValidationMapper'
import { CollaboratorsPanel } from './CollaboratorsPanel'
import { ChangeIndicatorOverlay, useRecentChanges } from './ChangeIndicator'
import { getNodeTypeColor, WorkflowNodeType, FlowVersionStatus } from '@features/adventures/types/workflow.types'
import { useApplyRemoteChanges } from '@hooks/useFlowCollaboration'
import { useAdventureCollaboration } from '@contexts/AdventureCollaborationContext'
import { useAuthStore } from '@store/authStore'
import { useAdventureEditorStore } from '@features/adventures/stores/adventureEditorStore'
import { usePublishedPermissions } from '@features/adventures/hooks/usePublishedPermissions'
import type { SectionId } from '../editor/AdventureRibbon'

// Node dimensions for collision detection (approximate based on BaseNode styling)
const NODE_WIDTH = 180
const NODE_HEIGHT = 80
const GRID_SIZE = 20

// Check if two node rectangles overlap
function nodesOverlap(
  pos1: { x: number; y: number },
  pos2: { x: number; y: number },
  padding = 0
): boolean {
  return !(
    pos1.x + NODE_WIDTH + padding < pos2.x ||
    pos2.x + NODE_WIDTH + padding < pos1.x ||
    pos1.y + NODE_HEIGHT + padding < pos2.y ||
    pos2.y + NODE_HEIGHT + padding < pos1.y
  )
}

// Find a non-overlapping position for a node
function findNonOverlappingPosition(
  nodeId: string,
  targetPos: { x: number; y: number },
  allNodes: WorkflowNode[]
): { x: number; y: number } {
  const otherNodes = allNodes.filter(n => n.id !== nodeId)

  // Check if current position is valid
  const hasOverlap = otherNodes.some(other =>
    nodesOverlap(targetPos, other.position)
  )

  if (!hasOverlap) {
    return targetPos
  }

  // Try positions in a spiral pattern around the target
  const directions = [
    { x: 1, y: 0 },   // right
    { x: 0, y: 1 },   // down
    { x: -1, y: 0 },  // left
    { x: 0, y: -1 },  // up
    { x: 1, y: 1 },   // diagonal
    { x: -1, y: 1 },
    { x: 1, y: -1 },
    { x: -1, y: -1 },
  ]

  for (let distance = 1; distance <= 10; distance++) {
    for (const dir of directions) {
      const testPos = {
        x: Math.round((targetPos.x + dir.x * (NODE_WIDTH + 20) * distance) / GRID_SIZE) * GRID_SIZE,
        y: Math.round((targetPos.y + dir.y * (NODE_HEIGHT + 20) * distance) / GRID_SIZE) * GRID_SIZE,
      }

      const testOverlap = otherNodes.some(other =>
        nodesOverlap(testPos, other.position)
      )

      if (!testOverlap) {
        return testPos
      }
    }
  }

  // Fallback: return original position snapped to grid
  return {
    x: Math.round(targetPos.x / GRID_SIZE) * GRID_SIZE,
    y: Math.round(targetPos.y / GRID_SIZE) * GRID_SIZE,
  }
}

interface WorkflowDesignerProps {
  adventureId: string
  // Editor ribbon props for mobile hamburger menu
  editorMenuProps?: {
    activeSection: SectionId
    onSectionChanged: (section: SectionId) => void
    onEditorSave?: () => void
    onPublish?: () => void
    onDelete?: () => void
    onHistory?: () => void
    onFlowVersionHistory?: () => void
    onImportFlow?: () => void
    isEditorSaving?: boolean
    isPublishing?: boolean
    isReadyToPublish?: boolean
    hiddenSections?: SectionId[]
    workflowAllowed?: boolean
  }
}

/**
 * Main workflow designer component.
 * Uses local-only editing with explicit save/compile workflow.
 */
export function WorkflowDesigner({ adventureId, editorMenuProps }: WorkflowDesignerProps) {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const { user } = useAuthStore()
  const [showMiniMap, setShowMiniMap] = useState(false)
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' }>({
    open: false,
    message: '',
    severity: 'success',
  })
  const [edgeDeletePosition, setEdgeDeletePosition] = useState<{ x: number; y: number } | null>(null)
  const [showValidationPanel, setShowValidationPanel] = useState(false)
  const [showCollaboratorsPanel, setShowCollaboratorsPanel] = useState(true)
  const [importError, setImportError] = useState<{ open: boolean; message: string; details: string }>({
    open: false,
    message: '',
    details: '',
  })
  // Compile context for ValidationPanel (when showing server compilation results)
  const [validationSource, setValidationSource] = useState<'local' | 'compile'>('local')
  const [compileStats, setCompileStats] = useState<CompileStatistics | undefined>(undefined)
  const [compileVersionNumber, setCompileVersionNumber] = useState<number | undefined>(undefined)
  const [showPublishConfirm, setShowPublishConfirm] = useState(false)

  // Get adventure owner info to determine if current user can publish
  const adventure = useAdventureEditorStore((state) => state.adventure)
  const categorySettings = useAdventureEditorStore((state) => state.categorySettings)
  const isOwner = user?.userId === adventure?.creatorId

  // Get published permissions - determines if workflow is read-only
  const { canEditWorkflow, isPublished } = usePublishedPermissions()
  const isReadOnly = !canEditWorkflow

  const {
    nodes,
    edges,
    selectedNodeId,
    selectedEdgeId,
    onNodesChange,
    onEdgesChange,
    onConnect,
    selectNode,
    selectEdge,
    deleteEdge,
    setAdventureId,
    loadWorkflow,
    setLoading,
    setError,
    setSaving,
    setDirty,
    setValidationResult,
    setVersionInfo,
    runValidation,
    validationResult,
    getSerializableState,
    loadFromJson,
    updateRouteConfig,
    isLoading,
    isSaving,
    error,
    isDirty,
    versionNumber,
    versionStatus,
    isCompiled,
    // Undo/Redo
    undo,
    redo,
    canUndo,
    canRedo,
    pushToUndoStack,
    // Collaboration
    incrementChangeSequence,
  } = useWorkflowStore()

  // Track previous edges to detect new route edge connections
  const prevEdgesRef = useRef<Edge[]>([])
  const [generatingRouteEdgeId, setGeneratingRouteEdgeId] = useState<string | null>(null)
  const [routePanelEdgeId, setRoutePanelEdgeId] = useState<string | null>(null)

  // Real-time collaboration via AdventureCollaboration context
  const { applyRemoteChange } = useApplyRemoteChanges()
  const { addChange, getChangeForNode, getDeleteChanges } = useRecentChanges()

  // Track when we're applying remote changes to avoid feedback loops
  // When a remote change is applied, we don't want to broadcast it back
  const isApplyingRemoteChangeRef = useRef(false)

  // Track when loading a version or importing to prevent broadcasting individual changes
  const isLoadingVersionRef = useRef(false)

  // Get collaboration context (connection is managed at AdventureEditor level)
  const {
    isConnected: isCollaborationConnected,
    isConnecting: isCollaborationConnecting,
    participants,
    sendNodeMove,
    sendNodeAdd,
    sendNodeDelete,
    sendNodeUpdate,
    sendEdgeAdd,
    sendEdgeDelete,
    sendWorkflowReplace,
    subscribeToRemoteChanges,
    subscribeToUserJoined,
    subscribeToUserLeft,
    subscribeToSyncRequest,
  } = useAdventureCollaboration()

  // Subscribe to remote changes for workflow updates
  useEffect(() => {
    const unsubscribeRemoteChanges = subscribeToRemoteChanges((change) => {
      // Only handle workflow-related changes
      const changeType = change.changeData.type
      if (!['node_add', 'node_move', 'node_delete', 'node_update',
           'edge_add', 'edge_delete', 'edge_update', 'bulk_change',
           'workflow_replace'].includes(changeType)) {
        return
      }

      console.log('[WorkflowDesigner] Remote change received:', changeType, change.userName)

      // For delete changes, capture the node position before it's removed
      let nodePosition: { x: number; y: number } | undefined
      if (change.changeData.type === 'node_delete') {
        const currentNodes = useWorkflowStore.getState().nodes
        const node = currentNodes.find(n => n.id === (change.changeData as { nodeId: string }).nodeId)
        if (node) {
          nodePosition = node.position
        }
      }

      // Mark that we're applying a remote change to prevent feedback loop
      isApplyingRemoteChangeRef.current = true

      // Apply changes from other users
      applyRemoteChange(change)

      // Reset the flag after a microtask to ensure state updates have propagated
      queueMicrotask(() => {
        isApplyingRemoteChangeRef.current = false
      })

      // Track the change for visual indicator
      addChange(change, nodePosition)
    })

    const unsubscribeUserJoined = subscribeToUserJoined((data) => {
      // Don't show notification if it's the current user joining
      if (data.userId === user?.userId) {
        return
      }
      setSnackbar({
        open: true,
        message: `${data.userName} joined the flow`,
        severity: 'info',
      })
    })

    const unsubscribeUserLeft = subscribeToUserLeft((data) => {
      // Don't show notification if it's the current user leaving
      if (data.userId === user?.userId) {
        return
      }
      setSnackbar({
        open: true,
        message: `${data.userName} left the flow`,
        severity: 'info',
      })
    })

    const unsubscribeSyncRequest = subscribeToSyncRequest(async (data) => {
      // Another user is requesting the current workflow state
      console.log('[WorkflowDesigner] Sending workflow sync to', data.requesterName)
      const { nodes, edges } = useWorkflowStore.getState()
      await sendWorkflowReplace(nodes, edges)
      setSnackbar({
        open: true,
        message: `Synced workflow with ${data.requesterName}`,
        severity: 'info',
      })
    })

    return () => {
      unsubscribeRemoteChanges()
      unsubscribeUserJoined()
      unsubscribeUserLeft()
      unsubscribeSyncRequest()
    }
  }, [subscribeToRemoteChanges, subscribeToUserJoined, subscribeToUserLeft,
      subscribeToSyncRequest, applyRemoteChange, addChange, sendWorkflowReplace, user?.userId])

  // Get resetWorkflow from store
  const resetWorkflow = useWorkflowStore((state) => state.resetWorkflow)

  // Set adventure ID on mount and reset workflow when adventure changes
  useEffect(() => {
    // Reset the workflow canvas when adventure ID changes to clear previous session's nodes
    resetWorkflow()
    setAdventureId(adventureId)
  }, [adventureId, setAdventureId, resetWorkflow])

  // Get initializeEmptyWorkflow from store
  const initializeEmptyWorkflow = useWorkflowStore((state) => state.initializeEmptyWorkflow)

  // Fetch workflow data
  const { data: workflowData, isLoading: isFetching, isError } = useQuery({
    queryKey: ['workflow', adventureId],
    queryFn: () => workflowDesignerApi.getFlow(adventureId),
    retry: false,
    enabled: !!adventureId,
    // Prevent automatic refetching - we manage state manually via the store
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  })

  // Load workflow data into store when fetched, or initialize with start node if no workflow exists
  useEffect(() => {
    setLoading(isFetching)
    if (workflowData) {
      loadWorkflow(workflowData)
    } else if (!isFetching && (isError || workflowData === null)) {
      // No workflow exists for this adventure - initialize with a default Start node
      initializeEmptyWorkflow()
    }
  }, [workflowData, isFetching, isError, loadWorkflow, setLoading, initializeEmptyWorkflow])

  // Auto-generate route preview when new route edges are created
  useEffect(() => {
    // Find new route edges by comparing current edges with previous
    const prevEdgeIds = new Set(prevEdgesRef.current.map(e => e.id))
    const newRouteEdges = edges.filter(
      edge => !prevEdgeIds.has(edge.id) && edge.data?.isRouteEdge
    )

    // Update ref for next comparison
    prevEdgesRef.current = edges

    // Generate preview routes for new route edges
    newRouteEdges.forEach(async (edge) => {
      const sourceNode = nodes.find(n => n.id === edge.source)
      const targetNode = nodes.find(n => n.id === edge.target)

      const sourceLocation = sourceNode?.data.poiLocationConfig
      const targetLocation = targetNode?.data.poiLocationConfig

      // Only generate if both nodes have valid locations
      if (
        sourceLocation?.latitude && sourceLocation?.longitude &&
        targetLocation?.latitude && targetLocation?.longitude
      ) {
        setGeneratingRouteEdgeId(edge.id)

        try {
          const result = await workflowDesignerApi.generateRoute(adventureId, {
            sourceLatitude: sourceLocation.latitude,
            sourceLongitude: sourceLocation.longitude,
            targetLatitude: targetLocation.latitude,
            targetLongitude: targetLocation.longitude,
            travelMode: (edge.data?.routeConfig as any)?.travelMode || 'foot-walking',
          })

          // Update the edge with preview route data
          updateRouteConfig(edge.id, {
            cachedRoute: {
              coordinates: result.coordinates,
              distanceMeters: result.distanceMeters,
              durationSeconds: result.durationSeconds,
              cachedAt: new Date().toISOString(),
              expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(), // 24hr preview expiry
            },
          })

          setSnackbar({
            open: true,
            message: `Route generated: ${(result.distanceMeters / 1000).toFixed(1)}km`,
            severity: 'success',
          })
        } catch (err) {
          console.error('Failed to generate route preview:', err)
          // Don't show error snackbar for preview failures - they can manually regenerate
        } finally {
          setGeneratingRouteEdgeId(null)
        }
      }
    })
  }, [edges, nodes, adventureId, updateRouteConfig])

  // Save workflow mutation (new architecture - saves entire flow)
  const saveMutation = useMutation({
    mutationFn: () => {
      const state = getSerializableState()
      return workflowDesignerApi.saveFlow(adventureId, state)
    },
    onMutate: () => {
      setSaving(true)
    },
    onSuccess: (data) => {
      loadWorkflow(data)
      setDirty(false)
      // NOTE: Don't invalidate the workflow query here - loadWorkflow already
      // loaded the response data. Invalidating would trigger a redundant refetch.
      setSnackbar({ open: true, message: 'Flow saved successfully', severity: 'success' })
    },
    onError: (error: Error) => {
      setError(error.message)
      setSnackbar({ open: true, message: `Failed to save: ${error.message}`, severity: 'error' })
    },
    onSettled: () => {
      setSaving(false)
    },
  })

  // Compile flow mutation - saves first then compiles
  const compileMutation = useMutation({
    retry: false, // Don't retry compile - it saves first, so retrying would re-save
    mutationFn: async () => {
      // First save the current state
      const state = getSerializableState()
      await workflowDesignerApi.saveFlow(adventureId, state)
      // Then compile
      return workflowDesignerApi.compileFlow(adventureId)
    },
    onMutate: () => {
      setSaving(true)
    },
    onSuccess: async (result) => {
      setDirty(false)

      // Update version info FIRST so ValidationPanel sees correct state for "Start Testing" button
      if (result.success) {
        setVersionInfo({
          versionNumber: result.versionNumber,
          status: FlowVersionStatus.Draft,
          isCompiled: true,
        })
      }

      // Map server validation result to client format and show in ValidationPanel
      if (result.validation) {
        const mappedValidation = mapServerValidationToClient(result.validation)
        setValidationResult(mappedValidation)
        setValidationSource('compile')
        setCompileStats(result.statistics)
        setCompileVersionNumber(result.versionNumber)
        setShowValidationPanel(true)
      }

      // Handle success/failure notifications
      if (result.success) {
        // NOTE: Don't invalidate the workflow query here - it would trigger a refetch
        // that overwrites our setVersionInfo state. We already have the correct state.

        // Notify other users about the compiled version
        const { nodes, edges } = useWorkflowStore.getState()
        await sendWorkflowReplace(nodes, edges)

        setSnackbar({
          open: true,
          message: `Compiled version ${result.versionNumber} successfully!`,
          severity: 'success',
        })
      } else {
        setSnackbar({
          open: true,
          message: `Compilation failed with ${result.validation?.errorCount || 0} error(s)`,
          severity: 'error',
        })
      }
    },
    onError: (error: Error) => {
      // Check if the error response contains compile validation details
      // (server may return non-200 status for failed compilations)
      const axiosError = error as any
      const responseData = axiosError?.response?.data
      if (responseData?.validation) {
        try {
          const mappedValidation = mapServerValidationToClient(responseData.validation)
          setValidationResult(mappedValidation)
          setValidationSource('compile')
          setCompileStats(responseData.statistics ?? undefined)
          setCompileVersionNumber(responseData.versionNumber)
          setShowValidationPanel(true)
          setSnackbar({
            open: true,
            message: `Compilation failed with ${responseData.validation?.errorCount || 0} error(s)`,
            severity: 'error',
          })
          return
        } catch {
          // Fall through to generic error
        }
      }
      setSnackbar({ open: true, message: `Compile error: ${error.message}`, severity: 'error' })
    },
    onSettled: () => {
      setSaving(false)
    },
  })

  // Start testing mutation
  const startTestingMutation = useMutation({
    mutationFn: () => workflowDesignerApi.startTesting(adventureId),
    onSuccess: async () => {
      setVersionInfo({
        versionNumber: versionNumber || 1,
        status: FlowVersionStatus.Testing,
        isCompiled: true,
      })
      setSnackbar({ open: true, message: 'Testing started', severity: 'success' })
      // NOTE: Don't invalidate the workflow query here - it would trigger a refetch
      // that overwrites our setVersionInfo state. We already have the correct state.

      // Notify other users about the status change
      const { nodes, edges } = useWorkflowStore.getState()
      await sendWorkflowReplace(nodes, edges)
    },
    onError: (error: Error) => {
      setSnackbar({ open: true, message: `Failed to start testing: ${error.message}`, severity: 'error' })
    },
  })

  // Publish mutation
  const publishMutation = useMutation({
    mutationFn: () => workflowDesignerApi.publishFlow(adventureId),
    onSuccess: async () => {
      setVersionInfo({
        versionNumber: versionNumber || 1,
        status: FlowVersionStatus.Published,
        isCompiled: true,
      })
      setSnackbar({ open: true, message: 'Adventure published successfully!', severity: 'success' })
      // NOTE: Don't invalidate the workflow query here - it would trigger a refetch
      // that overwrites our setVersionInfo state. We already have the correct state.

      // Notify other users about the publication
      const { nodes, edges } = useWorkflowStore.getState()
      await sendWorkflowReplace(nodes, edges)
    },
    onError: (error: Error) => {
      setSnackbar({ open: true, message: `Failed to publish: ${error.message}`, severity: 'error' })
    },
  })

  // Validate workflow mutation
  const validateMutation = useMutation({
    mutationFn: () => workflowDesignerApi.validateWorkflow(adventureId),
    onSuccess: (result) => {
      setValidationResult(result as any)
      if (result.isValid) {
        setSnackbar({ open: true, message: 'Workflow is valid!', severity: 'success' })
      } else {
        setSnackbar({
          open: true,
          message: `Validation failed: ${result.errors.join(', ')}`,
          severity: 'error',
        })
      }
    },
    onError: (error: Error) => {
      setSnackbar({ open: true, message: `Validation error: ${error.message}`, severity: 'error' })
    },
  })

  // Handle node changes with real-time sync
  // Push to undo stack for 'add' changes only - 'remove' is handled by deleteNode in store
  // Note: Node additions/deletions are tracked via prevNodeIdsRef effect, not here,
  // because toolbar uses addNodeLocal which bypasses React Flow's onNodesChange
  const handleNodesChange = useCallback(
    (changes: NodeChange<WorkflowNode>[]) => {
      // Block all changes when read-only (except selection changes for viewing)
      if (isReadOnly) {
        const selectionOnlyChanges = changes.filter(change => change.type === 'select')
        if (selectionOnlyChanges.length > 0) {
          onNodesChange(selectionOnlyChanges)
        }
        return
      }

      // Only push to undo stack for 'add' changes
      // 'remove' changes are handled by deleteNode action which has its own undo stack push
      const isAddChange = changes.some(change => change.type === 'add')
      if (isAddChange) {
        pushToUndoStack()
      }

      // Handle position changes - check for collisions when drag ends
      let hasMoves = false
      const modifiedChanges = changes.map(change => {
        if (change.type === 'position' && change.position && change.dragging === false) {
          // Check for collisions and find valid position
          const validPosition = findNonOverlappingPosition(change.id, change.position, nodes)

          // Broadcast the final position to other clients
          sendNodeMove(change.id, validPosition)
          hasMoves = true

          // Return modified change with collision-free position
          return {
            ...change,
            position: validPosition,
          }
        }
        return change
      })

      // Increment change sequence if we broadcasted any moves
      if (hasMoves) {
        incrementChangeSequence()
      }

      onNodesChange(modifiedChanges)
    },
    [onNodesChange, pushToUndoStack, sendNodeMove, incrementChangeSequence, isReadOnly, nodes]
  )

  // Handle connection with real-time sync
  const handleConnect = useCallback(
    (connection: Connection) => {
      // Block connections when read-only
      if (isReadOnly) return

      pushToUndoStack()
      onConnect(connection)

      // The edge is created by onConnect - we need to broadcast it
      // Since we don't have the edge ID yet, we'll broadcast after the state updates
      // This is handled in the edges effect below
    },
    [onConnect, pushToUndoStack, isReadOnly]
  )

  // Track node additions/deletions and broadcast to other clients
  // This is needed because toolbar uses addNodeLocal which bypasses React Flow's onNodesChange
  const prevNodeIdsRef = useRef<Set<string> | null>(null)
  useEffect(() => {
    const currentNodeIds = new Set(nodes.map(n => n.id))

    // Skip the first render (initial load) - don't broadcast existing nodes
    if (prevNodeIdsRef.current === null) {
      prevNodeIdsRef.current = currentNodeIds
      return
    }

    // Skip if this change came from a remote user (prevent feedback loop)
    // Also skip if loading a version/import (we broadcast a single workflow_replace instead)
    if (isApplyingRemoteChangeRef.current || isLoadingVersionRef.current) {
      prevNodeIdsRef.current = currentNodeIds
      return
    }

    // Find new nodes
    let hasChanges = false
    nodes.forEach(node => {
      if (!prevNodeIdsRef.current!.has(node.id)) {
        // New node added - broadcast to other clients
        sendNodeAdd(node)
        hasChanges = true
      }
    })

    // Find deleted nodes
    prevNodeIdsRef.current.forEach(nodeId => {
      if (!currentNodeIds.has(nodeId)) {
        // Node deleted - broadcast to other clients
        sendNodeDelete(nodeId)
        hasChanges = true
      }
    })

    // Increment change sequence if we broadcasted any changes
    if (hasChanges) {
      incrementChangeSequence()
    }

    prevNodeIdsRef.current = currentNodeIds
  }, [nodes, sendNodeAdd, sendNodeDelete, incrementChangeSequence])

  // Track edge additions and broadcast to other clients
  const prevEdgeIdsRef = useRef<Set<string> | null>(null)
  useEffect(() => {
    const currentEdgeIds = new Set(edges.map(e => e.id))

    // Skip the first render (initial load) - don't broadcast existing edges
    if (prevEdgeIdsRef.current === null) {
      prevEdgeIdsRef.current = currentEdgeIds
      return
    }

    // Skip if this change came from a remote user (prevent feedback loop)
    // Also skip if loading a version/import (we broadcast a single workflow_replace instead)
    if (isApplyingRemoteChangeRef.current || isLoadingVersionRef.current) {
      prevEdgeIdsRef.current = currentEdgeIds
      return
    }

    // Find new edges
    let hasChanges = false
    edges.forEach(edge => {
      if (!prevEdgeIdsRef.current!.has(edge.id)) {
        // New edge added - broadcast to other clients
        sendEdgeAdd(edge)
        hasChanges = true
      }
    })

    // Find deleted edges
    prevEdgeIdsRef.current.forEach(edgeId => {
      if (!currentEdgeIds.has(edgeId)) {
        // Edge deleted - broadcast to other clients
        sendEdgeDelete(edgeId)
        hasChanges = true
      }
    })

    // Increment change sequence if we broadcasted any changes
    if (hasChanges) {
      incrementChangeSequence()
    }

    prevEdgeIdsRef.current = currentEdgeIds
  }, [edges, sendEdgeAdd, sendEdgeDelete, incrementChangeSequence])

  // Track node data changes and broadcast to other clients
  const prevNodesDataRef = useRef<Map<string, string> | null>(null)
  useEffect(() => {
    // Create a map of nodeId -> serialized data (excluding position which is handled separately)
    const currentNodesData = new Map<string, string>()
    nodes.forEach(node => {
      // Serialize node data for comparison (exclude position-related fields)
      const dataToCompare = { ...node.data }
      currentNodesData.set(node.id, JSON.stringify(dataToCompare))
    })

    // Skip the first render (initial load) - don't broadcast existing node data
    if (prevNodesDataRef.current === null) {
      prevNodesDataRef.current = currentNodesData
      return
    }

    // Skip if this change came from a remote user (prevent feedback loop)
    // Also skip if loading a version/import (we broadcast a single workflow_replace instead)
    if (isApplyingRemoteChangeRef.current || isLoadingVersionRef.current) {
      prevNodesDataRef.current = currentNodesData
      return
    }

    // Find nodes with changed data
    let hasChanges = false
    currentNodesData.forEach((serializedData, nodeId) => {
      const prevData = prevNodesDataRef.current!.get(nodeId)
      if (prevData && prevData !== serializedData) {
        // Node data changed - broadcast to other clients
        const node = nodes.find(n => n.id === nodeId)
        if (node) {
          sendNodeUpdate(nodeId, node.data as Record<string, unknown>)
          hasChanges = true
        }
      }
    })

    // Increment change sequence if we broadcasted any changes
    if (hasChanges) {
      incrementChangeSequence()
    }

    prevNodesDataRef.current = currentNodesData
  }, [nodes, sendNodeUpdate, incrementChangeSequence])

  // Handle undo
  const handleUndo = useCallback(() => {
    undo()
  }, [undo])

  // Handle redo
  const handleRedo = useCallback(() => {
    redo()
  }, [redo])

  // Handle node selection
  const onNodeClick = useCallback(
    (_event: React.MouseEvent, node: WorkflowNode) => {
      selectNode(node.id)
    },
    [selectNode]
  )

  // Handle edge selection
  const onEdgeClick = useCallback(
    (event: React.MouseEvent, edge: Edge) => {
      selectEdge(edge.id)
      // Get position relative to the React Flow container
      const bounds = (event.currentTarget as HTMLElement).closest('.react-flow')?.getBoundingClientRect()
      if (bounds) {
        setEdgeDeletePosition({
          x: event.clientX - bounds.left,
          y: event.clientY - bounds.top,
        })
      }
    },
    [selectEdge]
  )

  // Handle pane click (deselect)
  const onPaneClick = useCallback(() => {
    selectNode(null)
    selectEdge(null)
    setEdgeDeletePosition(null)
  }, [selectNode, selectEdge])

  // Delete edge locally (no real-time sync)
  const handleDeleteEdge = useCallback((edgeId: string) => {
    // Block deletion when read-only
    if (isReadOnly) return

    pushToUndoStack()
    deleteEdge(edgeId)
    selectEdge(null)
    setEdgeDeletePosition(null)
  }, [deleteEdge, selectEdge, pushToUndoStack, isReadOnly])

  // Handle keyboard events for deletion and undo/redo
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Don't process if user is typing in an input field
      const target = event.target as HTMLElement
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
        return
      }

      // Block undo/redo/delete when read-only
      if (isReadOnly) return

      // Undo: Ctrl+Z / Cmd+Z
      if ((event.ctrlKey || event.metaKey) && event.key === 'z' && !event.shiftKey) {
        event.preventDefault()
        if (canUndo) {
          handleUndo()
        }
        return
      }

      // Redo: Ctrl+Y / Cmd+Y or Ctrl+Shift+Z / Cmd+Shift+Z
      if ((event.ctrlKey || event.metaKey) && (event.key === 'y' || (event.key === 'z' && event.shiftKey))) {
        event.preventDefault()
        if (canRedo) {
          handleRedo()
        }
        return
      }

      // Delete edge
      if (event.key === 'Delete' || event.key === 'Backspace') {
        if (selectedEdgeId) {
          handleDeleteEdge(selectedEdgeId)
        }
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [selectedEdgeId, handleDeleteEdge, canUndo, canRedo, handleUndo, handleRedo, isReadOnly])

  // Handle clear route from edge button (just clears local state)
  const handleClearEdgeRoute = useCallback(() => {
    if (!selectedEdgeId) return
    const selectedEdge = edges.find(e => e.id === selectedEdgeId)
    if (!selectedEdge?.data?.isRouteEdge) return

    updateRouteConfig(selectedEdgeId, {
      cachedRoute: undefined,
    })
    setSnackbar({ open: true, message: 'Route cleared', severity: 'success' })
  }, [selectedEdgeId, edges, updateRouteConfig])

  // Handle regenerate route from edge button (uses preview API - works for unsaved edges)
  const handleRegenerateEdgeRoute = useCallback(async () => {
    if (!selectedEdgeId) return
    const selectedEdge = edges.find(e => e.id === selectedEdgeId)
    if (!selectedEdge?.data?.isRouteEdge) return

    const sourceNode = nodes.find(n => n.id === selectedEdge.source)
    const targetNode = nodes.find(n => n.id === selectedEdge.target)
    const sourceLocation = sourceNode?.data.poiLocationConfig
    const targetLocation = targetNode?.data.poiLocationConfig

    if (!sourceLocation?.latitude || !sourceLocation?.longitude ||
        !targetLocation?.latitude || !targetLocation?.longitude) {
      setSnackbar({ open: true, message: 'Both nodes need valid locations', severity: 'error' })
      return
    }

    setGeneratingRouteEdgeId(selectedEdgeId)
    try {
      // Use preview API - works for both saved and unsaved edges
      const result = await workflowDesignerApi.generateRoute(adventureId, {
        sourceLatitude: sourceLocation.latitude,
        sourceLongitude: sourceLocation.longitude,
        targetLatitude: targetLocation.latitude,
        targetLongitude: targetLocation.longitude,
        travelMode: (selectedEdge.data?.routeConfig as any)?.travelMode || 'foot-walking',
      })

      updateRouteConfig(selectedEdgeId, {
        cachedRoute: {
          coordinates: result.coordinates,
          distanceMeters: result.distanceMeters,
          durationSeconds: result.durationSeconds,
          cachedAt: new Date().toISOString(),
          expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
        },
      })

      setSnackbar({
        open: true,
        message: `Route generated: ${(result.distanceMeters / 1000).toFixed(1)}km`,
        severity: 'success',
      })
    } catch (err) {
      console.error('Failed to generate route:', err)
      setSnackbar({ open: true, message: 'Failed to generate route', severity: 'error' })
    } finally {
      setGeneratingRouteEdgeId(null)
    }
  }, [selectedEdgeId, edges, nodes, adventureId, updateRouteConfig])

  // Handle save
  const handleSave = useCallback(() => {
    saveMutation.mutate()
  }, [saveMutation])

  // Handle validate - use local validation
  const handleValidate = useCallback(() => {
    // Reset to local validation context
    setValidationSource('local')
    setCompileStats(undefined)
    setCompileVersionNumber(undefined)

    const result = runValidation()
    setShowValidationPanel(true)
    if (result.isValid) {
      setSnackbar({ open: true, message: 'Workflow is valid!', severity: 'success' })
    } else {
      setSnackbar({
        open: true,
        message: `Found ${result.errorCount} error${result.errorCount !== 1 ? 's' : ''} and ${result.warningCount} warning${result.warningCount !== 1 ? 's' : ''}`,
        severity: result.errorCount > 0 ? 'error' : ('warning' as 'error'),
      })
    }
  }, [runValidation])

  // Handle node click from validation panel
  const handleValidationNodeClick = useCallback((nodeId: string) => {
    selectNode(nodeId)
    // TODO: Could add logic to pan/zoom to the selected node
  }, [selectNode])

  // Handle compile
  const handleCompile = useCallback(() => {
    compileMutation.mutate()
  }, [compileMutation])

  // Handle start testing
  const handleStartTesting = useCallback(() => {
    startTestingMutation.mutate()
  }, [startTestingMutation])

  // Handle publish - show confirmation dialog first
  const handlePublish = useCallback(() => {
    setShowPublishConfirm(true)
  }, [])

  // Confirm publish after user acknowledges warnings
  const handleConfirmPublish = useCallback(() => {
    setShowPublishConfirm(false)
    publishMutation.mutate()
  }, [publishMutation])

  // Close snackbar
  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }))
  }

  // Show loading state
  if (isLoading || isFetching) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
          minHeight: 400,
        }}
      >
        <CircularProgress />
      </Box>
    )
  }

  const selectedNode = nodes.find((n) => n.id === selectedNodeId)

  // Add selected styling to edges
  const styledEdges = edges.map((edge) => ({
    ...edge,
    selected: edge.id === selectedEdgeId,
    style: {
      ...edge.style,
      strokeWidth: edge.id === selectedEdgeId ? 4 : (edge.style?.strokeWidth || 2),
      stroke: edge.id === selectedEdgeId ? '#1976d2' : edge.style?.stroke,
    },
  }))

  return (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 400 }}>
        <WorkflowToolbar
          onSave={handleSave}
          onValidate={handleValidate}
          onCompile={handleCompile}
          onStartTesting={handleStartTesting}
          onPublish={handlePublish}
          isSaving={isSaving}
          isCompiling={compileMutation.isPending}
          isDirty={isDirty}
          versionNumber={versionNumber}
          versionStatus={versionStatus}
          isCompiled={isCompiled}
          isOwner={isOwner}
          isReadOnly={isReadOnly}
          showMiniMap={showMiniMap}
          onToggleMiniMap={() => setShowMiniMap(!showMiniMap)}
          onUndo={handleUndo}
          onRedo={handleRedo}
          canUndo={canUndo}
          canRedo={canRedo}
          mainCategory={adventure?.mainCategory}
          allowedNodes={categorySettings?.allowedNodes}
          editorMenuProps={editorMenuProps}
        />

        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ m: 1 }}>
            {error}
          </Alert>
        )}

        <Box
          sx={{
            flex: 1,
            position: 'relative',
            minHeight: 0,
            touchAction: 'none',
            '& .react-flow': {
              touchAction: 'none',
            },
          }}
        >
          <ReactFlow
            nodes={nodes}
            edges={styledEdges}
            onNodesChange={handleNodesChange}
            onEdgesChange={isReadOnly ? undefined : onEdgesChange}
            onConnect={isReadOnly ? undefined : handleConnect}
            onNodeClick={onNodeClick}
            onEdgeClick={onEdgeClick}
            onPaneClick={onPaneClick}
            nodeTypes={nodeTypes}
            fitView
            fitViewOptions={{ padding: 0.2 }}
            selectionMode={SelectionMode.Partial}
            selectNodesOnDrag={false}
            nodesDraggable={!isReadOnly}
            nodesConnectable={!isReadOnly}
            elementsSelectable={true}
            panOnDrag={true}
            panOnScroll={isMobile}
            zoomOnPinch={true}
            snapToGrid={true}
            snapGrid={[20, 20]}
            defaultEdgeOptions={{
              type: 'smoothstep',
              style: { strokeWidth: 2 },
            }}
            connectionLineStyle={{ strokeWidth: 2 }}
            proOptions={{ hideAttribution: true }}
          >
          {/* Arrow marker definition for edges */}
          <svg>
            <defs>
              <marker
                id="arrow"
                viewBox="0 0 10 10"
                refX="8"
                refY="5"
                markerWidth="6"
                markerHeight="6"
                orient="auto-start-reverse"
              >
                <path d="M 0 0 L 10 5 L 0 10 z" fill="currentColor" />
              </marker>
            </defs>
          </svg>
          <Background variant={BackgroundVariant.Dots} gap={16} size={1} />
          {showMiniMap && (
            <MiniMap
              nodeColor={(node: WorkflowNode) => getNodeTypeColor(node.data?.nodeType ?? WorkflowNodeType.Start)}
              maskColor="rgba(0, 0, 0, 0.1)"
              style={{ backgroundColor: 'rgba(255, 255, 255, 0.9)' }}
            />
          )}

          {/* Remote change indicators */}
          <ChangeIndicatorOverlay
            getChangeForNode={getChangeForNode}
            getDeleteChanges={getDeleteChanges}
          />

          {/* Dirty indicator */}
          {isDirty && (
            <Panel position="top-right">
              <Alert severity="info" sx={{ py: 0.5, px: 1, fontSize: { xs: '0.75rem', md: '0.875rem' } }}>
                Unsaved
              </Alert>
            </Panel>
          )}

          {/* Route generation indicator */}
          {generatingRouteEdgeId && (
            <Panel position="bottom-center">
              <Alert
                severity="info"
                icon={<CircularProgress size={16} />}
                sx={{ py: 0.5, px: 2, fontSize: { xs: '0.75rem', md: '0.875rem' } }}
              >
                Generating route...
              </Alert>
            </Panel>
          )}

          {/* Edge action buttons (shown near click position when edge is selected) - hidden in read-only mode */}
          {!isReadOnly && selectedEdgeId && edgeDeletePosition && (() => {
            const selectedEdge = edges.find(e => e.id === selectedEdgeId)
            const isRouteEdge = selectedEdge?.data?.isRouteEdge
            const hasRoute = !!(selectedEdge?.data?.routeConfig as any)?.cachedRoute
            const isGenerating = generatingRouteEdgeId === selectedEdgeId
            // Calculate button count for centering: route edges have 3-4 buttons, regular edges have 1
            const buttonCount = isRouteEdge ? (hasRoute ? 4 : 3) : 1
            const buttonWidth = 40 // small FAB width
            const gap = 4 // 0.5 * 8px
            const totalWidth = buttonCount * buttonWidth + (buttonCount - 1) * gap

            return (
              <Box
                sx={{
                  position: 'absolute',
                  left: edgeDeletePosition.x - totalWidth / 2,
                  top: edgeDeletePosition.y - 20,
                  zIndex: 1000,
                  display: 'flex',
                  gap: 0.5,
                }}
              >
                {/* Route-specific buttons */}
                {isRouteEdge && (
                  <>
                    <Tooltip title="Edit route properties">
                      <Fab
                        color="info"
                        size="small"
                        onClick={() => setRoutePanelEdgeId(selectedEdgeId)}
                        disabled={isGenerating}
                        sx={{ boxShadow: 3 }}
                      >
                        <EditIcon />
                      </Fab>
                    </Tooltip>
                    <Tooltip title={hasRoute ? "Regenerate route" : "Generate route"}>
                      <span>
                        <Fab
                          color="primary"
                          size="small"
                          onClick={handleRegenerateEdgeRoute}
                          disabled={isGenerating}
                          sx={{ boxShadow: 3 }}
                        >
                          {isGenerating ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />}
                        </Fab>
                      </span>
                    </Tooltip>
                    {hasRoute && (
                      <Tooltip title="Clear route">
                        <Fab
                          color="warning"
                          size="small"
                          onClick={handleClearEdgeRoute}
                          disabled={isGenerating}
                          sx={{ boxShadow: 3 }}
                        >
                          <ClearIcon />
                        </Fab>
                      </Tooltip>
                    )}
                  </>
                )}
                {/* Delete button (always shown) */}
                <Tooltip title="Delete connection">
                  <Fab
                    color="error"
                    size="small"
                    onClick={() => handleDeleteEdge(selectedEdgeId)}
                    disabled={isGenerating}
                    sx={{ boxShadow: 3 }}
                  >
                    <DeleteIcon />
                  </Fab>
                </Tooltip>
              </Box>
            )
          })()}
          </ReactFlow>
        </Box>

        {/* Node Properties Dialog */}
        {selectedNode && (
          <NodePropertiesPanel
            node={selectedNode}
            adventureId={adventureId}
            onClose={() => selectNode(null)}
            isReadOnly={isReadOnly}
            mainCategory={adventure?.mainCategory}
          />
        )}

        {/* Route Properties Dialog - shown when edit button is clicked */}
        {routePanelEdgeId && (() => {
          const routeEdge = edges.find(e => e.id === routePanelEdgeId)
          if (routeEdge?.data?.isRouteEdge) {
            return (
              <RoutePropertiesPanel
                edge={routeEdge}
                nodes={nodes}
                adventureId={adventureId}
                onClose={() => setRoutePanelEdgeId(null)}
              />
            )
          }
          return null
        })()}

        {/* Validation Panel - shown after validation or compile */}
        {showValidationPanel && validationResult && (
          <ValidationPanel
            onClose={() => setShowValidationPanel(false)}
            onNodeClick={handleValidationNodeClick}
            source={validationSource}
            compileStats={compileStats}
            versionNumber={compileVersionNumber}
            onStartTesting={handleStartTesting}
            canStartTesting={validationSource === 'compile' && validationResult.isValid && versionStatus === FlowVersionStatus.Draft}
          />
        )}

        {/* Collaborators Panel - real-time collaboration */}
        {showCollaboratorsPanel && (
          <CollaboratorsPanel
            participants={participants}
            currentUserId={user?.userId}
            isConnected={isCollaborationConnected}
            isConnecting={isCollaborationConnecting}
            onClose={() => setShowCollaboratorsPanel(false)}
          />
        )}

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        </Snackbar>

        {/* Import Error Dialog */}
        <Dialog
          open={importError.open}
          onClose={() => setImportError(prev => ({ ...prev, open: false }))}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ color: 'error.main' }}>
            Import Failed
          </DialogTitle>
          <DialogContent>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              {importError.message}
            </Typography>
            <Box
              sx={{
                mt: 2,
                p: 2,
                bgcolor: 'grey.100',
                borderRadius: 1,
                fontFamily: 'monospace',
                fontSize: '0.875rem',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                maxHeight: 300,
                overflow: 'auto',
              }}
            >
              {importError.details}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setImportError(prev => ({ ...prev, open: false }))}>
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {/* Publish Confirmation Dialog */}
        <Dialog
          open={showPublishConfirm}
          onClose={() => setShowPublishConfirm(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              bgcolor: 'warning.main',
              color: 'warning.contrastText',
            }}
          >
            <WarningIcon />
            Publish Adventure
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                This action is permanent and cannot be undone.
              </Typography>
            </Alert>

            <Typography variant="body1" paragraph>
              Publishing this adventure will make it available to users. Please understand the following:
            </Typography>

            <List dense>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <ErrorIcon color="error" fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary="No further edits allowed"
                  secondary="Once published, the workflow cannot be modified. The adventure will remain in its current state permanently."
                />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <ErrorIcon color="error" fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary="Cannot be deleted after users join"
                  secondary="If any users join this adventure, it cannot be deleted. You can only disable it to prevent new participants."
                />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <WarningIcon color="warning" fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary="Adventure can be disabled"
                  secondary="You can disable the adventure at any time to prevent new users from joining, but existing participants will still have access."
                />
              </ListItem>
            </List>

            <Divider sx={{ my: 2 }} />

            <Typography variant="body2" color="text.secondary">
              Make sure you have thoroughly tested the adventure before publishing. If you need to make changes, cancel this dialog and continue editing.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setShowPublishConfirm(false)}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="success"
              onClick={handleConfirmPublish}
              disabled={publishMutation.isPending}
              startIcon={publishMutation.isPending ? <CircularProgress size={16} color="inherit" /> : <CheckCircleIcon />}
            >
              {publishMutation.isPending ? 'Publishing...' : 'Yes, Publish Adventure'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
  )
}
